import { useRef, useCallback, useState, useEffect } from 'react';

/**
 * 节流 Hook，限制函数在指定时间间隔内只能执行一次
 * @param callback 要节流的回调函数
 * @param delay 节流间隔（毫秒），默认 5000ms
 * @param onThrottled 当被节流时调用的回调函数，接收剩余秒数作为参数
 * @returns 返回节流后的函数和节流状态
 */
export function useThrottle<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number = 5000,
  onThrottled?: (remainingSeconds: number) => void
): [T, boolean] {
  const lastExecutionTimeRef = useRef<number | null>(null);
  const [isThrottled, setIsThrottled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 清理 timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const throttledCallback = async (...args: Parameters<T>) => {
    const now = Date.now();
    const lastExecutionTime = lastExecutionTimeRef.current;
    const timeSinceLastExecution = lastExecutionTime !== null ? now - lastExecutionTime : Infinity;

    // 清除之前的 timeout（如果有）
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (timeSinceLastExecution < delay && lastExecutionTime !== null) {
      // 在节流期间，拒绝执行
      setIsThrottled(true);
      const remainingTime = delay - timeSinceLastExecution;
      const remainingSeconds = Math.ceil(remainingTime / 1000);

      // 调用节流回调
      if (onThrottled) {
        onThrottled(remainingSeconds);
      }

      // 等待剩余时间后解除节流状态
      timeoutRef.current = setTimeout(() => {
        setIsThrottled(false);
        timeoutRef.current = null;
      }, remainingTime);

      return;
    }

    // 允许执行，立即开始计时
    setIsThrottled(true);
    lastExecutionTimeRef.current = now; // 记录执行时间

    // 立即设置 timeout，在 delay 时间后解除节流（不等待 callback 执行完成）
    timeoutRef.current = setTimeout(() => {
      setIsThrottled(false);
      timeoutRef.current = null;
    }, delay);

    // 执行 callback（不阻塞节流计时）
    try {
      const result = await callback(...args);
      return result;
    } catch (error) {
      // 如果 callback 执行出错，不影响节流计时
      throw error;
    }
  };

  return [throttledCallback as T, isThrottled];
}
